import { Crown } from "lucide-react";

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useAuthor } from "@/hooks/useAuthor";
import { genUserName } from "@/lib/genUserName";
import type { MemberEntry } from "@/hooks/useMembers";

function MemberRow({ member }: { member: MemberEntry }) {
  const author = useAuthor(member.pubkey);
  const metadata = author.data?.metadata;
  const displayName =
    metadata?.name ?? metadata?.display_name ?? genUserName(member.pubkey);
  return (
    <li className="flex items-center gap-2.5 rounded-lg px-2 py-1.5 hover:bg-secondary/60">
      <Avatar className="size-8 border">
        <AvatarImage src={metadata?.picture} alt={displayName} />
        <AvatarFallback className="bg-primary/10 text-primary text-xs font-semibold">
          {displayName.slice(0, 2).toUpperCase()}
        </AvatarFallback>
      </Avatar>
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium leading-tight">
          {displayName}
        </p>
        {metadata?.nip05 && (
          <p className="truncate text-xs text-muted-foreground">{metadata.nip05}</p>
        )}
      </div>
      {member.isOwner && (
        <Crown className="size-4 shrink-0 text-gold" aria-label="Founder" />
      )}
    </li>
  );
}

export function MemberList({ members }: { members: MemberEntry[] }) {
  return (
    <aside className="hidden w-60 shrink-0 flex-col border-l bg-card lg:flex">
      <div className="border-b px-4 py-4">
        <h2 className="text-sm font-semibold">
          Members <span className="text-muted-foreground">({members.length})</span>
        </h2>
      </div>
      <ul className="flex-1 space-y-0.5 overflow-y-auto p-2">
        {members.map((m) => (
          <MemberRow key={m.pubkey} member={m} />
        ))}
      </ul>
    </aside>
  );
}
